import {
  Avatar,
  Button,
  Modal,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useUpdateMemberGovernance } from 'src/Hooks/Mutations/useUpdateMemberGovernance';
import { useUpdateOwnerGovernance } from 'src/Hooks/Mutations/useUpdateOwnerGovernance';
import { OperationalRole, OwnershipNature } from 'src/types/governance.types';

import styles from './EditMemberGovernanceModal.module.scss';
import { Props } from './EditMemberGovernanceModal.types';

const OPERATIONAL_ROLE_OPTIONS = [
  { value: OperationalRole.GESTIONNAIRE, label: 'Gestionnaire (Administre la maison)' },
  { value: OperationalRole.PROPRIETAIRE, label: 'Propriétaire / Associé (Droits légaux)' },
  { value: OperationalRole.VISITEUR, label: 'Invité / Visiteur (Réserve et partage les frais)' },
];

interface FormValues {
  operationalRole: OperationalRole | null;
  shares: number | null;
  ownershipNature: OwnershipNature | null;
  isMandate: boolean;
  hasVotingRights: boolean;
}

const EditMemberGovernanceModal = ({
  open,
  onClose,
  member,
  hasDismemberment,
  shareType,
  isOwner,
}: Props) => {
  const { t } = useTranslation();
  const { mutate: mutateMember, isLoading: isMemberLoading } = useUpdateMemberGovernance();
  const { mutate: mutateOwner, isLoading: isOwnerLoading } = useUpdateOwnerGovernance();
  const isLoading = isMemberLoading || isOwnerLoading;

  const form = useForm<FormValues>({
    initialValues: {
      operationalRole: null,
      shares: null,
      ownershipNature: null,
      isMandate: false,
      hasVotingRights: true,
    },
  });

  useEffect(() => {
    if (member && open) {
      const memberData = member as unknown as Record<string, unknown>;
      form.setValues({
        operationalRole: (memberData.operationalRole as OperationalRole) ?? null,
        shares: (memberData.shares as number) ?? null,
        ownershipNature: (memberData.ownershipNature as OwnershipNature) ?? null,
        isMandate: (memberData.isMandate as boolean) ?? false,
        hasVotingRights: (memberData.hasVotingRights as boolean) ?? true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member, open]);

  const isVisiteur = form.values.operationalRole === OperationalRole.VISITEUR;
  const showShares = !isVisiteur && form.values.operationalRole;
  const showMandate =
    form.values.operationalRole === OperationalRole.GESTIONNAIRE && form.values.shares === 0;
  const showOwnershipNature = hasDismemberment && showShares;
  const showVotingRights =
    showOwnershipNature && form.values.ownershipNature === OwnershipNature.USUFRUIT;

  const handleSave = () => {
    if (!member) return;

    if (isOwner) {
      mutateOwner(
        {
          ownerOperationalRole: form.values.operationalRole,
          ownerShares: isVisiteur ? null : form.values.shares,
          ownerOwnershipNature: showOwnershipNature ? form.values.ownershipNature : null,
          ownerHasVotingRights: showVotingRights ? form.values.hasVotingRights : null,
        },
        { onSuccess: () => onClose() }
      );
    } else {
      mutateMember(
        {
          membershipId: member.id,
          data: {
            operationalRole: form.values.operationalRole,
            shares: isVisiteur ? null : form.values.shares,
            ownershipNature: showOwnershipNature ? form.values.ownershipNature : null,
            isMandate: showMandate ? form.values.isMandate : null,
            hasVotingRights: showVotingRights ? form.values.hasVotingRights : null,
          },
        },
        { onSuccess: () => onClose() }
      );
    }
  };

  const shareLabel =
    shareType === 'PARTS_SOCIALES'
      ? 'Nombre de parts sociales'
      : shareType === 'TANTIEMES'
        ? 'Nombre de tantièmes'
        : 'Pourcentage (%)';

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={t('admin.governance.editMember', 'Rôle et répartition')}
      size="md"
    >
      {member && (
        <>
          <div className={styles.memberInfo}>
            <Avatar size="md" radius="xl">
              {member.user.firstName?.charAt(0) ?? '?'}
            </Avatar>
            <div>
              <div className={styles.memberName}>
                {member.user.firstName} {member.user.lastName}
              </div>
              <div className={styles.memberEmail}>{member.user.email}</div>
            </div>
          </div>

          <Stack gap="md">
            <Select
              label={t('admin.governance.operationalRole', 'Rôle opérationnel')}
              data={OPERATIONAL_ROLE_OPTIONS}
              value={form.values.operationalRole}
              onChange={value => form.setFieldValue('operationalRole', value as OperationalRole)}
              placeholder={t('admin.governance.selectRole', 'Choisir un rôle')}
            />

            {showShares && (
              <NumberInput
                label={shareLabel}
                value={form.values.shares ?? ''}
                onChange={value =>
                  form.setFieldValue('shares', typeof value === 'number' ? value : null)
                }
                min={0}
                placeholder="0"
              />
            )}

            {showMandate && (
              <Switch
                label={t(
                  'admin.governance.mandateLabel',
                  "Mandataire ou représentant légal d'un propriétaire ?"
                )}
                checked={form.values.isMandate}
                onChange={e => form.setFieldValue('isMandate', e.currentTarget.checked)}
                size="md"
              />
            )}

            {showOwnershipNature && (
              <div>
                <Text size="sm" fw={500} mb="xs">
                  {t('admin.governance.ownershipNature', 'Nature des parts')}
                </Text>
                <SegmentedControl
                  fullWidth
                  data={[
                    {
                      value: OwnershipNature.PLEINE_PROPRIETE,
                      label: 'Pleine Propriété',
                    },
                    { value: OwnershipNature.USUFRUIT, label: 'Usufruit' },
                    {
                      value: OwnershipNature.NUE_PROPRIETE,
                      label: 'Nue-Propriété',
                    },
                  ]}
                  value={form.values.ownershipNature ?? ''}
                  onChange={value =>
                    form.setFieldValue('ownershipNature', value as OwnershipNature)
                  }
                />
              </div>
            )}

            {showVotingRights && (
              <Switch
                label={t('admin.governance.votingRights', 'Droit de vote aux Assemblées Générales')}
                checked={form.values.hasVotingRights}
                onChange={e => form.setFieldValue('hasVotingRights', e.currentTarget.checked)}
                size="md"
              />
            )}
          </Stack>

          <div className={styles.buttons}>
            <Button variant="outline" onClick={onClose}>
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button onClick={handleSave} loading={isLoading}>
              {t('common.save', 'Enregistrer')}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default EditMemberGovernanceModal;
